import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { $api } from 'services/service';
import {
    Card,
    Descriptions,
    Table,
    Select,
    InputNumber,
    Button,
    Image,
    message,
    Space,
    Typography,
    Spin,
    Divider,
    Tooltip,
    Popconfirm
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useHistory } from 'react-router';
import fileDownload from 'js-file-download';
import moment from 'moment';

const { Title, Text } = Typography;

// ------------ Types (aligned with BE description) ------------
interface EvaluationDto {
    id: string;
    name?: string | null; // label/name of the criterion
    score?: number | null; // max score per criterion
    parentId?: string | null;
    // New fields from nested res
    children?: EvaluationDto[] | null;
    description?: string | null;
    type?: number | null;
    questionId?: string | null;
    parentName?: string | null;
}

interface QuestionDto {
    id: string;
    questionSuiteId: string;
    questionText: string;
    level?: number;
    imageUrl?: string | null;
    levelText?: string | null;
    evaluations?: EvaluationDto[]; // nested: parents -> children
}

interface EvaluationDetailDto {
    id?: string;
    studentEvaluationId?: string;
    evaluationCriteriaId?: string | null;
    evaluationName?: string | null;
    studentScore?: number | null; // not used for update
    score?: number | null; // student's score on this criterion
}

interface StudentEvaluationQuestionDto {
    studentEvaluationId?: string | null;
    questionId?: string | null;
    studentId: string;
    studentName: string;
    userInternalId?: string | null;
    classId?: string;
    classCodeName?: string | null;
    className?: string | null;
    evaluationDetails?: EvaluationDetailDto[];
}

interface StudentEvaluationForPractice {
    subjectTeachingExamId: string;
    subjectTeachingExamName: string;
    subjectTeachingId: string;
    subjectTeachingName: string;
    subjectId: string;
    subjectName: string;
    method: number; // Enum.ExamMethod
    // Some BE variants may attach evaluations only to the first question or to a top-level field
    evaluations?: EvaluationDto[];
    questions: QuestionDto[];
    students: StudentEvaluationQuestionDto[];
}

// Local UI model for each student row
interface StudentRow {
    key: string; // studentId
    studentId: string;
    studentName: string;
    userInternalId?: string | null;
    className?: string | null;
    classCodeName?: string | null;
    selectedQuestionId: string; // which question this row currently associates with
    // details by evaluationCriteriaId
    details: Record<string, { id?: string; evaluationCriteriaId?: string; evaluationName?: string | null; score: number | null }>;
}

// --------- Helpers to transform evaluations to 2-level grouped columns ---------
function groupEvaluationsTwoLevel(evals: EvaluationDto[] | undefined | null) {
    const list = evals || [];

    // Detect nested structure (parents include children array)
    const hasNested = list.some(p => Array.isArray(p.children) && (p.children?.length || 0) > 0);

    if (hasNested) {
        const parents = list; // parents provided directly
        const childrenMap: Record<string, EvaluationDto[]> = {};
        parents.forEach(p => {
            childrenMap[p.id] = (p.children || []) as EvaluationDto[];
        });
        return { parents, childrenMap };
    }

    // Fallback: flat list with parentId relationship
    const parents = list.filter(e => !e.parentId);
    const childrenMap: Record<string, EvaluationDto[]> = {};
    list.filter(e => !!e.parentId).forEach(c => {
        const pid = String(c.parentId);
        if (!childrenMap[pid]) childrenMap[pid] = [];
        childrenMap[pid].push(c);
    });
    return { parents, childrenMap };
}

function sumChildMax(children: EvaluationDto[] = []) {
    return children.reduce((acc, c) => acc + (Number(c.score ?? 0) || 0), 0);
}

// Get leaf criteria: prefer children if any parent has children; otherwise, use parents as leaves
function getLeafCriteria(evals: EvaluationDto[] | undefined | null): EvaluationDto[] {
    const { parents, childrenMap } = groupEvaluationsTwoLevel(evals);
    const anyChild = parents.some(p => (childrenMap[p.id] || []).length > 0);
    if (anyChild) {
        const leaves: EvaluationDto[] = [];
        parents.forEach(p => {
            const children = childrenMap[p.id] || [];
            if (children.length > 0) {
                leaves.push(...children);
            } else {
                leaves.push(p);
            }
        });
        return leaves;
    }
    return parents;
}

// Compute sum of student's scores for specific question's child criteria
function calcStudentSum(student: StudentRow, childIds: string[]) {
    const total = childIds.reduce((acc, id) => acc + (Number(student.details[id]?.score ?? 0) || 0), 0);
    // Fix floating point precision error by rounding to 2 decimal places
    return Math.round(total * 100) / 100;
}

function hasAnyNonNullScore(student: StudentRow, leafIds: string[]) {
    return leafIds.some(id => student.details[id] && student.details[id].score != null);
}

// Build request payload for UpdateQuestionEvaluation
function buildUpdatePayload(examId: string, students: StudentRow[], questions: QuestionDto[]) {
    // For each student, include details for their selected question only
    const questionMap = new Map<string, QuestionDto>();
    questions.forEach(q => questionMap.set(q.id, q));

    const payloadStudents = students.map(s => {
        const q = questionMap.get(s.selectedQuestionId);
        const evals = (q?.evaluations && q.evaluations.length > 0) ? q.evaluations : undefined;
        const leafs = getLeafCriteria(evals);
        const leafIds = leafs.map(l => l.id);

        // only include student if at least one score is provided
        if (!hasAnyNonNullScore(s, leafIds)) return null;

        const details = leafIds.map(cid => {
            const d = s.details[cid] || { score: null };
            const name = leafs.find(e => e.id === cid)?.name ?? null;
            return {
                id: d.id || null,
                evaluationCriteriaId: cid,
                evaluationName: d.evaluationName || name,
                score: d.score ?? null
            };
        });

        return {
            studentId: s.studentId,
            questionId: s.selectedQuestionId,
            details
        };
    }).filter(Boolean) as Array<{ studentId: string; questionId: string; details: any[] }>;

    return { examId, students: payloadStudents };
}

// Small memoized score input to reduce re-renders in cells
const ScoreInput: React.FC<{ value: number | null; max?: number; onChange: (v: number | null) => void; }> = React.memo(({ value, max, onChange }) => {
    return (
        <InputNumber
            className="w-24"
            min={0}
            max={max}
            step={0.25}
            value={value ?? null}
            onChange={(val) => onChange(val as number | null)}
        />
    );
});

// ------------ Component ------------
const PracticeExam: React.FC = () => {
    let history = useHistory();
    const params = useParams<any>();
    const examId: string | undefined = params?.examId || params?.id; // be flexible with param name

    const [loading, setLoading] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [data, setData] = useState<StudentEvaluationForPractice | null>(null);
    const [students, setStudents] = useState<StudentRow[]>([]);
    const [syncLoading, setSyncLoading] = useState<boolean>(false);
    const [exportLoading, setExportLoading] = useState<boolean>(false);

    // Fetch
    const load = async () => {
        if (!examId) return;
        setLoading(true);
        try {
            const res: any = await $api.studentEvaluation.getQuestionEvaluation({ id: examId });
            const result: StudentEvaluationForPractice = res?.result ?? res?.data?.result ?? res;
            setData(result || null);

            const questions = result?.questions || [];
            const defaultQuestionId = questions[0]?.id;

            const nextStudents: StudentRow[] = (result?.students || []).map(s => {
                const selectedQ = s.questionId || defaultQuestionId || '';
                const detailsMap: StudentRow['details'] = {};
                (s.evaluationDetails || []).forEach(d => {
                    const key = d.evaluationCriteriaId || '';
                    if (!key) return;
                    detailsMap[key] = {
                        id: d.id,
                        evaluationCriteriaId: d.evaluationCriteriaId || undefined,
                        evaluationName: d.evaluationName || null,
                        score: d.score === undefined ? (d as any)?.studentScore ?? null : d.score
                    };
                });
                return {
                    key: s.studentId,
                    studentId: s.studentId,
                    studentName: s.studentName,
                    userInternalId: s.userInternalId,
                    className: s.className,
                    classCodeName: s.classCodeName,
                    selectedQuestionId: selectedQ,
                    details: detailsMap
                } as StudentRow;
            });
            setStudents(nextStudents);
        } catch (e: any) {
            const errMsg = e?.response?.data?.errors?.exceptionMessage || e?.response?.data?.message || e?.message || 'Không thể tải dữ liệu kỳ thi';
            message.error(errMsg);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [examId]);

    const questions = data?.questions || [];

    // Group students by selected question
    const studentsByQuestion = useMemo(() => {
        const map = new Map<string, StudentRow[]>();
        for (const s of students) {
            const qId = s.selectedQuestionId;
            if (!map.has(qId)) map.set(qId, []);
            map.get(qId)!.push(s);
        }
        return map;
    }, [students]);

    // Stable handlers to avoid re-creating renderers
    const onChangeQuestionForStudent = useCallback((studentId: string, newQuestionId: string) => {
        setStudents(prev => prev.map(s => (s.studentId === studentId ? { ...s, selectedQuestionId: newQuestionId } : s)));
    }, []);

    const onChangeScore = useCallback((studentId: string, evaluationCriteriaId: string, evaluationName: string | null | undefined, value: number | null) => {
        setStudents(prev => prev.map(s => {
            if (s.studentId !== studentId) return s;
            const prevDetail = s.details[evaluationCriteriaId] || { score: null } as any;
            // Prevent unnecessary updates when value is the same
            if ((prevDetail.score ?? null) === (value ?? null)) return s;
            return {
                ...s,
                details: {
                    ...s.details,
                    [evaluationCriteriaId]: {
                        id: prevDetail.id,
                        evaluationCriteriaId,
                        evaluationName: prevDetail.evaluationName ?? evaluationName ?? null,
                        score: value === null || value === undefined ? null : value
                    }
                }
            };
        }));
    }, []);

    const handleSaveAll = async () => {
        if (!examId) return;
        try {
            setSaving(true);
            const payload = buildUpdatePayload(examId, students, questions);
            if (!payload.students.length) {
                message.info('Không có dữ liệu điểm để lưu');
                return;
            }
            await $api.studentEvaluation.updateQuestionEvaluation(payload);
            message.success('Cập nhật điểm tiêu chí thành công');
            // Reload to sync IDs/new details from server
            await load();
        } catch (e: any) {
            const errMsg = e?.response?.data?.errors?.exceptionMessage || e?.response?.data?.message || e?.message || 'Cập nhật thất bại';
            message.error(errMsg);
        } finally {
            setSaving(false);
        }
    };

    const handleSyncScores = async () => {
        if (!examId) { message.error('Không tìm thấy kỳ thi'); return; }
        try {
            setSyncLoading(true);
            const questionsMap = new Map<string, QuestionDto>();
            (questions || []).forEach(q => questionsMap.set(q.id, q));
            const payload: Array<{ SubjectTeachingExamId: string; StudentId: string; Result: number | null }> = [];
            for (const s of students) {
                const q = questionsMap.get(s.selectedQuestionId);
                const evals = (q?.evaluations && q.evaluations.length > 0) ? q.evaluations : (data?.evaluations || []);
                const leafs = getLeafCriteria(evals);
                const leafIds = leafs.map(l => l.id);
                if (leafIds.length === 0) continue; // không có tiêu chí
                const anyNonNull = leafIds.some(id => s.details[id] && s.details[id].score != null);
                if (!anyNonNull) continue; // toàn null -> bỏ qua
                const total = leafIds.reduce((acc, id) => acc + (Number(s.details[id]?.score ?? 0) || 0), 0);
                payload.push({ SubjectTeachingExamId: examId!, StudentId: s.studentId, Result: total });
            }
            if (payload.length === 0) { message.info('Không có dữ liệu điểm để đồng bộ'); return; }
            await $api.exam.exam_save_list(payload);
            message.success(`Đồng bộ điểm thành công (${payload.length} sinh viên)`);
        } catch (e: any) {
            const errMsg = e?.response?.data?.errors?.exceptionMessage || e?.response?.data?.message || e?.message || 'Đồng bộ thất bại';
            message.error(errMsg);
        } finally { setSyncLoading(false); }
    };

    const handleExportPracticeQuestion = async () => {
        if (!examId) {
            message.error('Không tìm thấy kỳ thi');
            return;
        }
        try {
            setExportLoading(true);
            const res: any = await $api.studentEvaluation.exportPracticeQuestion(examId);
            if (res) {
                const fileName = `Bang_Diem_Thuc_Hanh_${moment(new Date()).format('DD_MM_YYYY')}.xlsx`;
                fileDownload(res, fileName);
                message.success('Xuất bảng điểm thành công!');
            }
        } catch (e: any) {
            const errMsg = e?.response?.data?.errors?.exceptionMessage || e?.response?.data?.message || e?.message || 'Có lỗi khi xuất bảng điểm';
            message.error(errMsg);
        } finally {
            setExportLoading(false);
        }
    };

    // Precompute shared options for question select (lightweight, avoids rendering heavy option nodes per row)
    const questionOptions = useMemo(() => {
        return (questions || []).map((qq, i) => ({
            value: qq.id,
            label: `Câu ${i + 1}`
        }));
    }, [questions]);

    // Build table columns per question and memoize them to avoid re-creating renderers each render
    const columnsByQuestion = useMemo(() => {
        const map = new Map<string, ColumnsType<StudentRow>>();
        const fallbackEvals = data?.evaluations || [];

        for (const q of questions) {
            const evals = (q.evaluations && q.evaluations.length > 0) ? q.evaluations : fallbackEvals;
            const { parents, childrenMap } = groupEvaluationsTwoLevel(evals);
            const hasAnyChild = parents.some(p => (childrenMap[p.id] || []).length > 0);
            const parentWithChildren = parents.filter(p => (childrenMap[p.id] || []).length > 0);
            const orphanParents = parents.filter(p => (childrenMap[p.id] || []).length === 0);
            const leafIdsForQ = getLeafCriteria(evals).map(e => e.id);

            const baseCols: ColumnsType<StudentRow> = [
                {
                    title: 'Sinh viên',
                    key: 'student',
                    fixed: 'left',
                    width: 200,
                    align: 'left',
                    shouldCellUpdate: (record, prev) => (
                        record.studentName !== (prev as any).studentName ||
                        record.userInternalId !== (prev as any).userInternalId ||
                        record.classCodeName !== (prev as any).classCodeName
                    ),
                    render: (_v, record) => (
                        <div className="flex flex-col break-words">
                            <span className="font-medium line-clamp-1" title={record.studentName}>{record.studentName}</span>
                            <span className="text-gray-500 text-xs"><i>{record.userInternalId || ''}{record.classCodeName ? ` | ${record.classCodeName}` : ''}</i></span>
                        </div>
                    )
                },
                {
                    title: 'Câu hỏi',
                    key: 'questionSelect',
                    width: 200,
                    shouldCellUpdate: (record, prev) => record.selectedQuestionId !== (prev as any).selectedQuestionId,
                    render: (_v, record) => {
                        const idx = (questions || []).findIndex(qq => qq.id === record.selectedQuestionId);
                        const selected = (questions || []).find(qq => qq.id === record.selectedQuestionId);
                        return (
                            <Tooltip
                                title={
                                    selected ? (
                                        <div style={{ maxWidth: 480, whiteSpace: 'pre-wrap' }}>
                                            <div className="font-medium mb-1">{`Câu ${idx + 1}`}</div>
                                            <div>{selected.questionText}</div>
                                            {selected.imageUrl ? (
                                                <div className="mt-2 text-center">
                                                    <Image src={selected.imageUrl} width={160} alt="question" />
                                                </div>
                                            ) : null}
                                        </div>
                                    ) : null
                                }
                                placement="topLeft"
                            >
                                <Select
                                    className="w-full"
                                    value={record.selectedQuestionId}
                                    onChange={(val) => onChangeQuestionForStudent(record.studentId, val)}
                                    optionLabelProp="label"
                                    options={questionOptions}
                                    popupMatchSelectWidth={false}
                                    showSearch={false}
                                />
                            </Tooltip>
                        );
                    }
                }
            ];

            let criteriaCols: ColumnsType<StudentRow>;

            if (hasAnyChild) {
                const groupedCols: ColumnsType<StudentRow> = parentWithChildren.map(parent => {
                    const childs = childrenMap[parent.id] || [];
                    const maxSum = sumChildMax(childs);
                    return {
                        title: (
                            <div>
                                <div className="font-medium">{parent.name}</div>
                                <div className="text-xs text-gray-500">Tổng tối đa: {maxSum}</div>
                            </div>
                        ),
                        align: 'center' as const,
                        children: childs.map(child => ({
                            title: (
                                <div className="text-xs">
                                    <div>{child.name}</div>
                                    {child.score != null ? <div className="text-gray-400">({child.score})</div> : null}
                                </div>
                            ),
                            dataIndex: child.id,
                            key: child.id,
                            align: 'center' as const,
                            width: 120,
                            shouldCellUpdate: (record, prev) => {
                                const prevVal = (prev as StudentRow).details?.[child.id]?.score ?? null;
                                const currVal = record.details?.[child.id]?.score ?? null;
                                return prevVal !== currVal;
                            },
                            render: (_v, record) => (
                                <ScoreInput
                                    max={child.score ?? undefined}
                                    value={record.details?.[child.id]?.score ?? null}
                                    onChange={(val) => onChangeScore(record.studentId, child.id, child.name, val)}
                                />
                            )
                        })) as any
                    } as any;
                });

                const orphanCols: ColumnsType<StudentRow> = orphanParents.map(op => ({
                    title: (
                        <div className="text-xs">
                            <div>{op.name}</div>
                            {op.score != null ? <div className="text-gray-400">({op.score})</div> : null}
                        </div>
                    ),
                    dataIndex: op.id,
                    key: op.id,
                    align: 'center' as const,
                    width: 120,
                    shouldCellUpdate: (record, prev) => {
                        const prevVal = (prev as StudentRow).details?.[op.id]?.score ?? null;
                        const currVal = record.details?.[op.id]?.score ?? null;
                        return prevVal !== currVal;
                    },
                    render: (_v, record) => (
                        <ScoreInput
                            max={op.score ?? undefined}
                            value={record.details?.[op.id]?.score ?? null}
                            onChange={(val) => onChangeScore(record.studentId, op.id, op.name, val)}
                        />
                    )
                }));

                criteriaCols = [...groupedCols, ...orphanCols];
            } else {
                criteriaCols = parents.map(p => ({
                    title: (
                        <div className="text-xs">
                            <div>{p.name}</div>
                            {p.score != null ? <div className="text-gray-400">({p.score})</div> : null}
                        </div>
                    ),
                    dataIndex: p.id,
                    key: p.id,
                    align: 'center' as const,
                    width: 120,
                    shouldCellUpdate: (record, prev) => {
                        const prevVal = (prev as StudentRow).details?.[p.id]?.score ?? null;
                        const currVal = record.details?.[p.id]?.score ?? null;
                        return prevVal !== currVal;
                    },
                    render: (_v, record) => (
                        <ScoreInput
                            max={p.score ?? undefined}
                            value={record.details?.[p.id]?.score ?? null}
                            onChange={(val) => onChangeScore(record.studentId, p.id, p.name, val)}
                        />
                    )
                }));
            }

            const totalCol: ColumnsType<StudentRow>[number] = {
                title: 'Tổng điểm',
                key: 'total',
                width: 90,
                fixed: 'right',
                align: 'center',
                shouldCellUpdate: (record, prev) => {
                    const prevSum = calcStudentSum(prev as StudentRow, leafIdsForQ);
                    const currSum = calcStudentSum(record, leafIdsForQ);
                    return prevSum !== currSum;
                },
                render: (_v, record) => <Text strong>{calcStudentSum(record, leafIdsForQ)}</Text>
            };

            map.set(q.id, [...baseCols, ...criteriaCols, totalCol]);
        }
        return map;
    }, [questions, data?.evaluations, onChangeScore, onChangeQuestionForStudent]);

    // Optional per-question save (only students currently in that question)
    const handleSaveForQuestion = async (questionId: string) => {
        if (!examId) return;
        try {
            setSaving(true);
            const subset = students.filter(s => s.selectedQuestionId === questionId);
            const payload = buildUpdatePayload(examId, subset, questions);
            if (!payload.students.length) {
                message.info('Không có dữ liệu điểm để lưu cho bảng này');
                return;
            }
            await $api.studentEvaluation.updateQuestionEvaluation(payload);
            message.success('Cập nhật bảng câu hỏi thành công');
            await load();
        } catch (e: any) {
            const errMsg = e?.response?.data?.errors?.exceptionMessage || e?.response?.data?.message || e?.message || 'Cập nhật thất bại';
            message.error(errMsg);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="p-4">
            <div className='___title__box mb-4'>
                <a className="font-bold inline-flex items-center gap-2" href={'#'} onClick={(event) => { event.preventDefault(); history.goBack(); }}>
                    <i className="fas fa-arrow-left" />
                    Quay lại
                </a>
            </div>
            <div className="flex items-center justify-between mb-4">
                <Title level={2} className="!mb-0 uppercase">Nhập điểm Thực hành</Title>
                <Space>
                    <Button type="primary" loading={saving} onClick={handleSaveAll}>Lưu tất cả</Button>
                    <Popconfirm title="Xác nhận đồng bộ điểm tổng?" okText="Đồng bộ" cancelText="Hủy" onConfirm={handleSyncScores}>
                        <Button loading={syncLoading}>Đồng bộ điểm</Button>
                    </Popconfirm>
                    <Button
                        type= "dashed"
                        loading={exportLoading}
                        onClick={handleExportPracticeQuestion}
                        icon={<i className="fas fa-file-excel mr-1"></i>}
                    >
                        Xuất bảng điểm
                    </Button>
                </Space>
            </div>

            <Spin spinning={loading} tip="Đang tải...">
                {data ? (
                    <>
                        <Card className="mb-4" title="Thông tin kỳ thi">
                            <Descriptions column={1} size="small">
                                <Descriptions.Item label="Kỳ thi">{data.subjectTeachingExamName}</Descriptions.Item>
                                <Descriptions.Item label="Môn">{data.subjectName}</Descriptions.Item>
                                <Descriptions.Item label="Học phần">{data.subjectTeachingName}</Descriptions.Item>
                                <Descriptions.Item label="Phương thức">{data.method}</Descriptions.Item>
                            </Descriptions>
                        </Card>

                        {(questions || []).length === 0 ? (
                            <Card>
                                <Text>Không có câu hỏi thực hành để chấm. (Kỳ thi chưa có bộ câu hỏi?)</Text>
                            </Card>
                        ) : null}

                        {(questions || []).map((q, idx) => {
                            const qStudents = studentsByQuestion.get(q.id) || [];
                            const columns = columnsByQuestion.get(q.id) || [];
                            const evals = (q.evaluations && q.evaluations.length > 0) ? q.evaluations : (data?.evaluations || []);
                            const leafs = getLeafCriteria(evals);
                            const maxTotal = leafs.reduce((acc, c) => acc + (Number(c.score ?? 0) || 0), 0);


                            return (
                                <Card key={q.id} className="mb-6" styles={{ body: { paddingTop: 16 } }}>
                                    <div className='flex mb-3'>
                                        <div className='flex flex-col w-full'>
                                            <Title level={5} className='!mb-0'>Câu hỏi {idx + 1}:</Title>
                                            <Text className='font-medium whitespace-pre-line'>{q.questionText}</Text>
                                            {q.imageUrl && (
                                                <div className='flex justify-center mt-3 w-full'>
                                                    <Image
                                                        src={q.imageUrl}
                                                        alt='question'
                                                        className='mx-auto'
                                                        style={{  maxWidth: '600px', height: 'auto', objectFit: 'contain' }}
                                                    />
                                                </div>
                                            )}
                                            <div className='text-gray-500 text-xs mt-2'>Tổng điểm tối đa: {maxTotal}</div>
                                        </div>
                                    </div>
                                    <Divider className="my-2" />
                                    <div className="mb-2 flex items-center justify-between">
                                        <Text type="secondary">Số sinh viên: {qStudents.length}</Text>
                                        <Button type="primary" onClick={() => handleSaveForQuestion(q.id)} loading={saving}>Lưu</Button>
                                    </div>
                                    <Table<StudentRow>
                                        rowKey="studentId"
                                        size="small"
                                        columns={columns}
                                        dataSource={qStudents}
                                        pagination={false}
                                        scroll={{ x: 'max-content' }}
                                        bordered
                                    />
                                </Card>
                            );
                        })}
                    </>
                ) : (
                    <Card>
                        <Text>Không có dữ liệu</Text>
                    </Card>
                )}
            </Spin>
        </div>
    );
};

export default PracticeExam;
